import { makeStyles, mergeClasses, Spinner } from "@fluentui/react-components";
import { useBoolean } from "@fluentui/react-hooks";
import {
  ColumnFiltersState,
  SortingState,
  Updater,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { StaticGlobalFilterContext } from "../../../context/StaticGlobalFilterContext";
import { FlightCardTypes } from "../../../enum/flights/FlightEnum";
import {
  AllFlightView,
  FlightTable,
  TimeMode,
} from "../../../enum/flights/FlightTableEnum";
import { FlightLegTypeEnum } from "../../../enum/FlightsEnum";
import { useApiResource } from "../../../hooks/useApiResource";
import { IGlobalDateFilter } from "../../../models/filters/IFilters";
import { IFlightTableState } from "../../../models/flights/IFlightsCommon";
import {
  AIRPORT_CODE_PARAMETER,
  EMPTY_STATE_MESSAGE,
} from "../../../utils/common/Constants";
import {
  displayAllFlightsView,
  getfilteredData,
  getFlightLegTypeCount,
  transformColumns,
} from "../../../utils/flights/FlightDetailHelperFunctions";
import { NoDataCard } from "../../common/no-data-card/NoDataCard";
import { allFlightTableColumns } from "./flight-table/column-config/allFlightsColumnConfig";
import { flightTableColumns } from "./flight-table/column-config/turnAroundColumnConfig";
import FlightDetailsTable from "./flight-table/FlightDetailsTable";
import FlightDetailsHeader from "./FlightDetailsHeader";

const getfilteredColumns = (columns: any[], legType: FlightLegTypeEnum) => {
  switch (legType) {
    case FlightLegTypeEnum.Arrival:
      return columns.filter((column) => {
        const columnType = column.id?.split(".")[0];
        return (
          columnType === "common" || columnType === FlightLegTypeEnum.Arrival
        );
      });
    case FlightLegTypeEnum.Departure:
      return columns.filter((column) => {
        const columnType = column.id?.split(".")[0];
        return (
          columnType === "common" || columnType === FlightLegTypeEnum.Departure
        );
      });
    default:
      return columns;
  }
};

export interface IFlightDetailsProps {
  viewType: FlightCardTypes;
  airport: string;
  tableDateFilter: IGlobalDateFilter;
  isScheduleTimeChecked: boolean;
  handleCheckboxChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

//Define the base URL and the Flights Table URL
const domain = process.env.REACT_APP_API_BASE_URL;
const flightsTableEndPoint = process.env.REACT_APP_API_FLIGHTSTABLE;
const allFlightsTableEndPoint = process.env.REACT_APP_API_ALLFLIGHTSTABLE;

const flightsTableBaseUrl = `${domain}${flightsTableEndPoint}`;
const allFlightsTableBaseUrl = `${domain}${allFlightsTableEndPoint}`;

const defaultState: IFlightTableState = {
  [FlightLegTypeEnum.Turnaround]: { filters: [], sorting: [] },
  [FlightLegTypeEnum.Arrival]: { filters: [], sorting: [] },
  [FlightLegTypeEnum.Departure]: { filters: [], sorting: [] },
  [FlightLegTypeEnum.All]: { filters: [], sorting: [] },
};

export default function Flight360Details({
  viewType,
  airport,
  tableDateFilter,
  isScheduleTimeChecked,
  handleCheckboxChange,
}: IFlightDetailsProps) {
  const classes = useStyles();

  const { timezone } = useContext(StaticGlobalFilterContext);

  const [isScreenCalloutVisible, { toggle: toggleIsScreenCalloutVisible }] =
    useBoolean(false);
  const [legType, setLegType] = useState<FlightLegTypeEnum>(
    FlightLegTypeEnum.Turnaround
  );

  const [checkboxes, setCheckboxes] = useState<any>([]);
  const [customfilteredColumns, setCustomfilteredColumns] =
    useState<any>(flightTableColumns);
  const [resfreshTable, setRefreshTable] = useState<Date>(new Date());

  //Retain filter and sorting state for each legtype type
  const [tableStates, setTableStates] =
    useState<IFlightTableState>(defaultState);

  const timeMode: TimeMode = isScheduleTimeChecked ? "Scheduled" : "Best";
  const isLegTypeAll = legType === FlightLegTypeEnum.All;
  const showAllFlightsView = displayAllFlightsView(viewType);

  const customViewType =
    viewType === FlightCardTypes.ADDITIONAL_DELAY
      ? FlightCardTypes.TURNAROUND_SUFFICIENCY
      : viewType;

  const payload = {
    start: format(tableDateFilter.startDate, "yyyy-MM-dd'T'HH:mm:ss"),
    end: format(tableDateFilter.endDate, "yyyy-MM-dd'T'HH:mm:ss"),
    timeMode,
    viewType: customViewType,
    tz: timezone,
  };

  const flightTableDependencies: string[] = [
    airport,
    viewType,
    timeMode,
    timezone,
    resfreshTable.toISOString(),
    tableDateFilter.startDate.toISOString(),
    tableDateFilter.endDate.toISOString(),
  ];

  const flightsTableUrl = flightsTableBaseUrl.replace(
    AIRPORT_CODE_PARAMETER,
    airport
  );
  const allFlightsTableUrl = allFlightsTableBaseUrl.replace(
    AIRPORT_CODE_PARAMETER,
    airport
  );

  //Turnaround Flights Data Fetch
  const {
    data: flighData = [],
    showLoader: isLoading,
    isError: turnaroundError,
  } = useApiResource<FlightTable[]>({
    queryKey: ["flightTable", ...flightTableDependencies],
    url: flightsTableUrl,
    fetchOnPost: true,
    fetchPayload: payload,
    refetchOnWindowFocus: false,
    keepPreviousDataOnFetch: true,
  });

  //All Flights Data Fetch
  const {
    data: allFlightsData = [],
    showLoader: allFlightsDataFetching,
    isError: allFlightsError,
  } = useApiResource<AllFlightView[]>({
    queryKey: ["allFlightTable", ...flightTableDependencies],
    url: allFlightsTableUrl,
    fetchOnPost: true,
    fetchPayload: payload,
    refetchOnWindowFocus: false,
    enabled: showAllFlightsView,
    keepPreviousDataOnFetch: true,
  });

  const loading = isLoading || allFlightsDataFetching;
  const error = turnaroundError || allFlightsError;

  const { arrival, departure, turnaround } = getFlightLegTypeCount(flighData);
  const allCount = allFlightsData.length;

  const filteredData = useMemo(
    () => getfilteredData(flighData, legType),
    [flighData, legType]
  );

  const sortedAllFlightsData = useMemo(() => {
    return allFlightsData.slice().sort((a, b) => {
      return a.blockTime && b.blockTime
        ? new Date(a.blockTime).getTime() - new Date(b.blockTime).getTime()
        : 0;
    });
  }, [allFlightsData]);

  const noDataCardContent = (
    <div style={{ margin: "auto" }}>
      <NoDataCard message={EMPTY_STATE_MESSAGE.Flights} />
    </div>
  );

  const tableColumnList = isLegTypeAll
    ? allFlightTableColumns
    : flightTableColumns;

  const tabledata: any[] = isLegTypeAll ? sortedAllFlightsData : filteredData;
  const hasTableData = tabledata.length > 0;

  const activeTableContext = useMemo(
    () => ({ legType, activeState: tableStates[legType] }),
    [tableStates, legType]
  );

  const onLegChangeHandler = useCallback(
    (buttonId: FlightLegTypeEnum) => {
      const columnList =
        buttonId === FlightLegTypeEnum.All
          ? allFlightTableColumns
          : flightTableColumns;
      const transformedColumns: any = transformColumns(
        columnList,
        buttonId,
        viewType
      );
      const filteredColumns = getfilteredColumns(columnList, buttonId);

      const checkedColumns = filteredColumns.filter((customColumn: any) =>
        transformedColumns.some(
          (checkbox: any) => checkbox.id === customColumn.id && checkbox.checked
        )
      );

      setCheckboxes(transformedColumns);
      setCustomfilteredColumns(checkedColumns);
      setLegType(buttonId);
    },
    [viewType]
  );

  const handleColumnCheckboxChange = useCallback((id: any) => {
    setCheckboxes((prevCheckboxes: any) =>
      prevCheckboxes.map((checkbox: any) =>
        checkbox.id === id
          ? { ...checkbox, checked: !checkbox.checked }
          : checkbox
      )
    );
  }, []);

  const handleTableStateChange = useCallback(
    (
      leg: FlightLegTypeEnum,
      filtersUpdater: Updater<ColumnFiltersState>,
      sortingUpdater: Updater<SortingState>
    ) => {
      setTableStates((prev) => {
        const prevForLeg = prev[leg] ?? { filters: [], sorting: [] };

        const nextFilters =
          typeof filtersUpdater === "function"
            ? filtersUpdater(prevForLeg.filters)
            : filtersUpdater;

        const nextSorting =
          typeof sortingUpdater === "function"
            ? sortingUpdater(prevForLeg.sorting)
            : sortingUpdater;

        const updated: IFlightTableState = {
          ...prev,
          [leg]: {
            filters: Array.isArray(nextFilters) ? [...nextFilters] : [],
            sorting: Array.isArray(nextSorting) ? [...nextSorting] : [],
          },
        };

        try {
          localStorage.setItem("tableStates", JSON.stringify(updated));
        } catch {}
        return updated;
      });
    },
    []
  );

  const onDefaultHandler = (_event: React.MouseEvent<HTMLButtonElement>) => {};

  const onApplyHandler = useCallback(
    (_event: React.MouseEvent<HTMLButtonElement>) => {
      const filteredColumns = getfilteredColumns(tableColumnList, legType);
      const checkedColumns = filteredColumns.filter((customColumn: any) =>
        checkboxes.some(
          (checkbox: any) => checkbox.id === customColumn.id && checkbox.checked
        )
      );
      setCustomfilteredColumns(checkedColumns);
      toggleIsScreenCalloutVisible();
    },
    [tableColumnList, legType, checkboxes, toggleIsScreenCalloutVisible]
  );

  const onResetHandler = useCallback(
    (_event: React.MouseEvent<HTMLButtonElement>) => {
      const filteredColumns = getfilteredColumns(tableColumnList, legType);
      setCustomfilteredColumns(filteredColumns);
      setCheckboxes(transformColumns(tableColumnList, legType, viewType));
    },
    [tableColumnList, legType, viewType]
  );

  useEffect(() => {
    //reset tableState when airport changes
    setTableStates(defaultState);
  }, [airport, viewType]);

  useEffect(() => {
    if (viewType === FlightCardTypes.REGULATED_FLIGHT) {
      onLegChangeHandler(FlightLegTypeEnum.Departure);
    } else {
      onLegChangeHandler(
        showAllFlightsView
          ? FlightLegTypeEnum.All
          : FlightLegTypeEnum.Turnaround
      );
    }
  }, [viewType, showAllFlightsView]);

  return (
    <section
      className={mergeClasses(
        classes.container,
        "bg--white pa-sm-2 br-8 d-flex amd-container-vcolumn"
      )}
      data-testid="flight-details"
    >
      <FlightDetailsHeader
        viewType={viewType}
        selectedButton={legType}
        isScheduleTimeChecked={isScheduleTimeChecked}
        legTypeCount={{
          all: allCount,
          arrival,
          departure,
          turnaround,
        }}
        handleButtonClick={onLegChangeHandler}
        handleCheckboxChange={handleCheckboxChange}
        transformColumns={checkboxes}
        handleColumnCheckboxChange={handleColumnCheckboxChange}
        onResetHandler={onResetHandler}
        onDefaultHandler={onDefaultHandler}
        onApplyHandler={onApplyHandler}
        isScreenCalloutVisible={isScreenCalloutVisible}
        toggleIsScreenCalloutVisible={toggleIsScreenCalloutVisible}
        setRefreshTable={setRefreshTable}
      />
      {error && noDataCardContent}
      {loading && (
        <Spinner
          labelPosition="below"
          label="Loading..."
          style={{ height: "486px" }}
        />
      )}
      {!error && !loading && hasTableData && (
        <FlightDetailsTable
          key={legType}
          airportCode={airport}
          flighData={tabledata}
          tableColumns={customfilteredColumns}
          tableContext={activeTableContext}
          onStateChange={handleTableStateChange}
        />
      )}
      {!error && !loading && !hasTableData && noDataCardContent}
    </section>
  );
}

const useStyles = makeStyles({
  container: {
    height: "555px",
  },
});
